import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import styled, { css } from 'styled-components';
import { AiLoadingText } from '@ardoq/ai';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { colors, radius, s8 } from '@ardoq/design-tokens';
import { type I18nLocale } from '@ardoq/i18n';
import {
  buildRichTextEditorLabels,
  RichTextEditor,
  type RichTextEditorExtension,
  type RichTextEditorLabels,
} from '@ardoq/rich-text-editor';
import { useGenerateComponentDescription } from './useGenerateComponentDescription';
import { aiDescriptionProvenanceOperations } from './types';
import type {
  AiDescriptionGeneration,
  AiDescriptionProvenance,
  ComponentDescriptionTracking,
  ComponentToDescribe,
} from './types';

const AI_BADGE_LABEL_VAR = '--ai-badge-label';

/**
 * Renders the transient "Generated by AI" pill inline, directly after the end
 * of the description. Applied as a CSS `::after` on the last block child of the
 * rich text editor's content (`.ProseMirror`) so it flows with the text.
 * Targeting the last *block* (`:last-child`) rather than the last paragraph
 * (`p:last-of-type`) keeps the pill at the true end of the content even when
 * the generated text ends with a list or heading. The label is passed in via a
 * CSS variable so the same styled wrapper stays locale-agnostic.
 */
const WithInlineAiBadge = styled.div<{ $show: boolean }>`
  ${({ $show }) =>
    $show &&
    css`
      .ProseMirror > :last-child::after {
        content: '✦ ' var(${AI_BADGE_LABEL_VAR}, 'Generated by AI');
        display: inline-block;
        margin-left: 6px;
        padding: 0 ${s8};
        border-radius: ${radius.medium};
        background: ${colors.surfaceInfo};
        color: ${colors.textSubtle};
        font-size: 11px;
        line-height: 18px;
        white-space: nowrap;
        vertical-align: middle;
        user-select: none;
      }
    `}
`;

/**
 * A CSS string literal, for the `content` of the badge's `::after`. JSON
 * escaping happens to line up for today's translations but is not CSS
 * escaping: a newline is `\A`, not `\n`.
 */
const toCssStringLiteral = (value: string): string =>
  `"${value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\A ')}"`;

const getAiBadgeLabelStyle = (badgeLabel: string): CSSProperties =>
  ({
    [AI_BADGE_LABEL_VAR]: toCssStringLiteral(badgeLabel),
  }) as CSSProperties;

const DEFAULT_MIN_HEIGHT = '120px';

type SharedProps = {
  richTextEditorLabels: RichTextEditorLabels;
  value: string;
  onValueChange: (markdown: string) => void;
  i18nLocale: I18nLocale;
  toolbarExtensions?: RichTextEditorExtension[];
  workspaceId?: string;
  minHeight?: string;
  label?: string;
  ariaLabel?: string;
  autoFocus?: boolean;
};

export type ComponentDescriptionEditorProps = Omit<
  SharedProps,
  'richTextEditorLabels'
> & {
  component: ComponentToDescribe;
  aiGeneration: AiDescriptionGeneration;
  /**
   * Applies the generated text. Separate from `onValueChange` so hosts can mark
   * the value as AI-provenanced (untouched) rather than user-edited.
   */
  onGenerated: (text: string) => void;
  /**
   * Reports the full generation window so hosts can gate a Save action while
   * the AI is writing.
   */
  onGeneratingChange?: (isGenerating: boolean) => void;
};

type AiComponentDescriptionEditorProps = Omit<
  ComponentDescriptionEditorProps,
  'aiGeneration'
> &
  Pick<SharedProps, 'richTextEditorLabels'> & {
    tracking: ComponentDescriptionTracking;
  };

const AiComponentDescriptionEditor = ({
  value,
  onValueChange,
  component,
  onGenerated,
  onGeneratingChange,
  i18nLocale,
  toolbarExtensions,
  workspaceId,
  minHeight = DEFAULT_MIN_HEIGHT,
  label,
  ariaLabel,
  autoFocus,
  richTextEditorLabels,
  tracking,
}: AiComponentDescriptionEditorProps) => {
  // True from the very first render when an empty description will
  // auto-generate, cleared once generation settles (success or failure). Note
  // this must not become an `isDisabled` toggle on the editor: changing that
  // prop on a live instance recreates the underlying tiptap editor, and the
  // toolbar's `useEditorState` then deep-compares `editor.state.selection`
  // across two editor instances (a deep/cyclic ProseMirror structure),
  // overflowing the stack. The editor mounts once, enabled, and stays — the
  // generation state is shown inside its content area instead.
  const [isAutoGenerating, setIsAutoGenerating] = useState(() => !value);
  const [aiProvenance, setAiProvenance] = useState<AiDescriptionProvenance>(
    aiDescriptionProvenanceOperations.initial
  );

  // The editor stays enabled while the AI writes, so the user can type into
  // it before the response lands. Whatever they wrote wins: applying the
  // generated text over it would both lose the edit and leave the host holding
  // a different value than the one on screen. Cleared when a generation
  // starts, so a regenerate after an edit still applies.
  const hasUserEditedSinceGenerationStarted = useRef(false);

  // Kept in a ref so the callback handed to the hook has a stable identity and
  // the hook's memoisation holds. Hosts routinely pass a fresh closure — the
  // component overview builds one per stream emission.
  const onGeneratedRef = useRef(onGenerated);
  useEffect(() => {
    onGeneratedRef.current = onGenerated;
  });

  const applyGeneratedText = useCallback((text: string) => {
    if (hasUserEditedSinceGenerationStarted.current) return;
    setAiProvenance(aiDescriptionProvenanceOperations.onGenerated());
    onGeneratedRef.current(text);
  }, []);

  const {
    generateDescription,
    isLoading,
    hasError,
    errorMessage,
    warningMessage,
    isMissingItemToDescribe,
    labels,
  } = useGenerateComponentDescription({
    component,
    currentValue: value,
    i18nLocale,
    tracking,
    onGenerated: applyGeneratedText,
  });

  const startGeneration = (): Promise<void> => {
    hasUserEditedSinceGenerationStarted.current = false;
    return generateDescription();
  };

  // Auto-generate once on mount for an empty description. Regeneration is left
  // to the explicit generate button. Guarded by a ref rather than the empty
  // dependency array alone: StrictMode double-invokes mount effects in
  // development, and a second generation is a second billed LLM call.
  const hasTriggeredAutoGeneration = useRef(false);
  useEffect(() => {
    if (hasTriggeredAutoGeneration.current) return;
    hasTriggeredAutoGeneration.current = true;
    if (!value) {
      startGeneration()
        .catch(() => undefined)
        .finally(() => setIsAutoGenerating(false));
    } else {
      setIsAutoGenerating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only trigger
  }, []);

  // Report the full busy window (not just isLoading): during the initial
  // auto-generate tick the loader is up but isLoading hasn't flipped yet, and
  // Save must stay disabled so it can't persist the empty value / tear the
  // editor down mid-generation.
  useEffect(() => {
    onGeneratingChange?.(isLoading || isAutoGenerating);
  }, [isLoading, isAutoGenerating, onGeneratingChange]);

  const handleInputChange = (markdownString: string) => {
    hasUserEditedSinceGenerationStarted.current = true;
    setAiProvenance(aiDescriptionProvenanceOperations.onUserEdit);
    onValueChange(markdownString);
  };

  const isGenerating = isLoading || isAutoGenerating;
  const showAiBadge = aiProvenance === 'untouched' && !isGenerating;

  return (
    <WithInlineAiBadge
      $show={showAiBadge}
      style={getAiBadgeLabelStyle(labels.generatedByAiBadge)}
    >
      <RichTextEditor
        content={value}
        onInputChange={handleInputChange}
        toolbarExtensions={toolbarExtensions}
        workspaceId={workspaceId}
        minHeight={minHeight}
        label={label}
        ariaLabel={ariaLabel}
        autoFocus={autoFocus}
        errorMessage={errorMessage}
        warningMessage={warningMessage}
        labels={richTextEditorLabels}
        inlineStatus={
          isGenerating ? (
            <AiLoadingText
              hasBorder={false}
              label={labels.generatingDescription}
            />
          ) : undefined
        }
        generateDescriptionProps={
          isGenerating
            ? undefined
            : {
                onClick: startGeneration,
                isLoading,
                hasError,
                isMissingItemToDescribe,
                missingItemTooltip: labels.missingItemTooltip,
                infoTooltip: labels.generateDescriptionTooltip,
                label: labels.generateLabel,
              }
        }
      />
      {/* The visual pill is a decorative CSS `::after`, which screen readers
          do not reliably expose. This companion carries the same provenance
          into the accessibility tree. Rendered outside `.ProseMirror` so it
          never enters the contenteditable region. */}
      {showAiBadge && (
        <VisuallyHidden>{labels.generatedByAiBadge}</VisuallyHidden>
      )}
    </WithInlineAiBadge>
  );
};

const PlainDescriptionEditor = ({
  value,
  onValueChange,
  toolbarExtensions,
  workspaceId,
  minHeight = DEFAULT_MIN_HEIGHT,
  label,
  ariaLabel,
  autoFocus,
  richTextEditorLabels,
}: SharedProps) => (
  <RichTextEditor
    content={value}
    onInputChange={onValueChange}
    toolbarExtensions={toolbarExtensions}
    workspaceId={workspaceId}
    minHeight={minHeight}
    label={label}
    ariaLabel={ariaLabel}
    autoFocus={autoFocus}
    labels={richTextEditorLabels}
  />
);

/**
 * The component-description editor, shared by every surface that edits a
 * component description (component overview, workspace sidebar, inventory
 * grid). With AI generation enabled it auto-generates on mount for an empty
 * description and offers explicit (re)generation afterwards; without it, it is
 * a plain rich text editor.
 */
export const ComponentDescriptionEditor = (
  props: ComponentDescriptionEditorProps
) => {
  const { aiGeneration, ...rest } = props;
  const richTextEditorLabels = useMemo(
    () => buildRichTextEditorLabels(props.i18nLocale),
    [props.i18nLocale]
  );

  if (!aiGeneration.isEnabled) {
    return (
      <PlainDescriptionEditor
        {...rest}
        richTextEditorLabels={richTextEditorLabels}
      />
    );
  }

  return (
    <AiComponentDescriptionEditor
      {...rest}
      richTextEditorLabels={richTextEditorLabels}
      tracking={aiGeneration.tracking}
    />
  );
};
